import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Container, Row, Col, Alert, Spinner, Button } from "react-bootstrap";
import HeaderTwo from "../../components/HeaderTwo";
import { BreadcrumbBox } from "../../components/common/Breadcrumb";
import FooterTwo from "../../components/FooterTwo";
import { Styles } from "./styles/event.js";
import { EVENTS_ENDPOINT, EVENT_LIKE_ENDPOINT } from "../../config/api";

const PLACEHOLDER_IMAGE = "https://via.placeholder.com/600x400?text=Event";

const useQuery = () => {
  const { search } = useLocation();
  return useMemo(() => new URLSearchParams(search), [search]);
};

const Events = () => {
  const query = useQuery();
  const searchTerm = query.get("search") || "";
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [liking, setLiking] = useState({});
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    totalItems: 0,
    totalPages: 1,
  });

  useEffect(() => {
    setPage(1);
  }, [searchTerm]);

  useEffect(() => {
    const controller = new AbortController();

    const loadEvents = async () => {
      setLoading(true);
      setError("");
      try {
        const params = new URLSearchParams({ limit: "10", page: String(page) });
        if (searchTerm.trim()) {
          params.set("search", searchTerm.trim());
        }
        const response = await fetch(
          `${EVENTS_ENDPOINT}?${params.toString()}`,
          { signal: controller.signal }
        );
        if (!response.ok) {
          const payload = await response.json().catch(() => ({}));
          throw new Error(payload.message || "Unable to load events.");
        }
        const payload = await response.json();
        setEvents(Array.isArray(payload.events) ? payload.events : []);
        if (payload.pagination) {
          const serverPage = payload.pagination.page || page;
          setPagination({
            page: serverPage,
            limit: payload.pagination.limit || 10,
            totalItems:
              payload.pagination.totalItems || payload.events?.length || 0,
            totalPages: payload.pagination.totalPages || 1,
          });
          if (serverPage !== page) {
            setPage(serverPage);
          }
        } else {
          setPagination({
            page,
            limit: 10,
            totalItems: payload.events?.length || 0,
            totalPages: 1,
          });
        }
      } catch (err) {
        if (err.name !== "AbortError") {
          setError(err.message || "Unable to load events.");
          setEvents([]);
          setPagination({ page: 1, limit: 10, totalItems: 0, totalPages: 1 });
        }
      } finally {
        if (!controller.signal.aborted) {
          setLoading(false);
        }
      }
    };

    loadEvents();

    return () => controller.abort();
  }, [searchTerm, page]);

  const handleLike = async (eventId, slug) => {
    if (!slug || liking[slug]) {
      return;
    }
    setLiking((prev) => ({ ...prev, [slug]: true }));
    try {
      const response = await fetch(EVENT_LIKE_ENDPOINT(slug), {
        method: "POST",
      });
      if (!response.ok) {
        const payload = await response.json().catch(() => ({}));
        throw new Error(payload.message || "Unable to like event.");
      }
      const payload = await response.json();
      setEvents((prev) =>
        prev.map((event) =>
          event._id === eventId
            ? { ...event, likes: payload.likes ?? (event.likes || 0) + 1 }
            : event
        )
      );
    } catch (err) {
      console.error(err);
    } finally {
      setLiking((prev) => {
        const next = { ...prev };
        delete next[slug];
        return next;
      });
    }
  };

  return (
    <Styles>
      <div className="main-wrapper event-page">
        <HeaderTwo />
        <BreadcrumbBox title="Events" />

        <section className="event-page-area">
          <Container>
            <Row>
              <Col lg="12" md="12">
                {loading && (
                  <div className="text-center my-5">
                    <Spinner animation="border" role="status" />
                  </div>
                )}
                {!loading && error && <Alert variant="danger">{error}</Alert>}
                {!loading && !error && events.length === 0 && (
                  <Alert variant="info">
                    {searchTerm
                      ? `No events found for "${searchTerm}".`
                      : "No published events available yet."}
                  </Alert>
                )}
                {!loading && !error && events.length > 0 && (
                  <>
                    {events.map((event) => {
                      const image = event.coverImage || PLACEHOLDER_IMAGE;
                      const startDateObj = event.startDate
                        ? new Date(event.startDate)
                        : null;
                      const endDateObj = event.endDate
                        ? new Date(event.endDate)
                        : null;
                      const dateOptions = {
                        dateStyle: "medium",
                        timeStyle: "short",
                      };

                      const timeZoneName =
                        Intl.DateTimeFormat().resolvedOptions().timeZone;

                      const startLabel = startDateObj
                        ? `${startDateObj.toLocaleString(
                            undefined,
                            dateOptions
                          )} (${timeZoneName})`
                        : "TBA";

                      const endLabel = endDateObj
                        ? `${endDateObj.toLocaleString(
                            undefined,
                            dateOptions
                          )} (${timeZoneName})`
                        : "";

                      const slugPath = event.slug
                        ? `/events/${event.slug}`
                        : "#";
                      const likes =
                        typeof event.likes === "number" ? event.likes : 0;
                      return (
                        <div className="event-box" key={event._id}>
                          <Row>
                            <Col xl="3" lg="4" md="12">
                              <div className="event-img">
                                <Link to={process.env.PUBLIC_URL + slugPath}>
                                  <img
                                    src={image}
                                    alt={event.title}
                                    className="img-fluid"
                                  />
                                </Link>
                              </div>
                            </Col>
                            <Col xl="9" lg="8" md="12">
                              <div className="event-content">
                                <div className="content-box">
                                  <Row>
                                    <Col md="9">
                                      <div className="event-title">
                                        <h6>
                                          <Link
                                            to={
                                              process.env.PUBLIC_URL + slugPath
                                            }
                                          >
                                            {event.title}
                                          </Link>
                                        </h6>
                                      </div>
                                      <div className="event-time-location">
                                        <ul className="list-unstyled list-inline">
                                          <li className="list-inline-item">
                                            <i className="las la-clock"></i>{" "}
                                            {startLabel}
                                            {endLabel ? ` - ${endLabel}` : ""}
                                          </li>
                                          {event.location && (
                                            <li className="list-inline-item">
                                              <i className="las la-map-marker"></i>{" "}
                                              {event.location}
                                            </li>
                                          )}
                                        </ul>
                                      </div>
                                      <div className="event-desc">
                                        <p>
                                          {event.description ||
                                            "More details coming soon."}
                                        </p>
                                      </div>
                                    </Col>
                                    <Col md="3" className="text-center">
                                      <div className="event-date">
                                        <p>
                                          {startDateObj
                                            ? startDateObj.toLocaleDateString(
                                                undefined,
                                                {
                                                  day: "2-digit",
                                                  month: "short",
                                                  year: "numeric",
                                                }
                                              )
                                            : "TBA"}
                                        </p>
                                      </div>
                                      <div className="like-btn">
                                        <Button
                                          variant="outline-success"
                                          onClick={() =>
                                            handleLike(event._id, event.slug)
                                          }
                                          disabled={
                                            !event.slug ||
                                            Boolean(liking[event.slug])
                                          }
                                        >
                                          <i className="las la-heart"></i>{" "}
                                          {liking[event.slug]
                                            ? "Liking..."
                                            : "Like"}{" "}
                                          ({likes})
                                        </Button>
                                      </div>
                                    </Col>
                                  </Row>
                                </div>
                              </div>
                            </Col>
                          </Row>
                        </div>
                      );
                    })}
                    <div className="pagination-controls mt-4">
                      <div className="page-info">
                        Page {pagination.page} of {pagination.totalPages} ·{" "}
                        {pagination.totalItems} total
                      </div>
                      <div className="page-actions">
                        <Button
                          variant="outline-secondary"
                          onClick={() =>
                            setPage((prev) => Math.max(prev - 1, 1))
                          }
                          disabled={pagination.page <= 1 || loading}
                        >
                          Previous
                        </Button>
                        <Button
                          variant="outline-secondary"
                          onClick={() =>
                            setPage((prev) =>
                              Math.min(prev + 1, pagination.totalPages)
                            )
                          }
                          disabled={
                            pagination.page >= pagination.totalPages || loading
                          }
                        >
                          Next
                        </Button>
                      </div>
                    </div>
                  </>
                )}
              </Col>
            </Row>
          </Container>
        </section>

        <FooterTwo />
      </div>
    </Styles>
  );
};

export default Events;
